import { Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Search01Icon, ViewIcon } from '@hugeicons/core-free-icons';
import { CaseService } from '../../core/services/case.service';
import { SnackbarService } from '../../core/services/snackbar.service';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';

interface AssistantCase {
    id: string | number;
    caseCode?: string;
    clientName: string;
    attorney: string;
    caseType: string;
    hospital: string;
    lienBalance: number;
    caseProgress: number;
    offerStatus?: string;
}

@Component({
    selector: 'app-assistant-cases',
    standalone: true,
    imports: [CommonModule, CurrencyPipe, HugeiconsIconComponent, EmptyStateComponent],
    templateUrl: './assistant-cases.component.html',
    styleUrl: './assistant-cases.component.scss'
})
export class AssistantCasesComponent implements OnInit {
    cases: AssistantCase[] = [];
    filteredCases: AssistantCase[] = [];
    searchTerm = '';

    // Icons
    searchIcon = Search01Icon;
    viewIcon = ViewIcon;

    constructor(
        private router: Router,
        private caseService: CaseService,
        private snackbar: SnackbarService
    ) {}

    ngOnInit() {
        this.loadCases();
    }

    loadCases() {
        this.caseService.getAssistantCaseList().subscribe({
            next: (data) => {
                const items = Array.isArray(data) ? data : (data?.data || []);
                this.cases = items.map((item: any) => this.mapCase(item));
                this.applyFilter();
            },
            error: () => {
                this.snackbar.show('Failed to load cases', 'error');
            }
        });
    }

    private readonly progressSteps: { label: string; variant: string }[] = [
        { label: 'Submitted', variant: 'warning' },
        { label: 'Offer Generated', variant: 'orange' },
        { label: 'Offer Status', variant: 'info' },
        { label: 'Patient Payment', variant: 'purple' },
        { label: 'Hospital Payment', variant: 'teal' },
        { label: 'Release of Lien', variant: 'cyan' },
        { label: 'Pending Trust Funds', variant: 'success' }
    ];

    private mapCase(item: any): AssistantCase {
        return {
            id: item.case_id || item.id,
            caseCode: item.case_code,
            clientName: item.client_name,
            attorney: item.attorney_name,
            caseType: item.type || item.case_type,
            hospital: item.hospital,
            lienBalance: parseFloat(item.lien_balance) || 0,
            caseProgress: parseInt(item.case_progress || item.current_stage, 10) || 0,
            offerStatus: item.offer_status || null
        };
    }

    getProgressStatus(item: AssistantCase): { label: string; variant: string } | null {
        const progress = item.caseProgress || 0;

        if (item.offerStatus === 'REJECTED') {
            return { label: 'Offer Rejected', variant: 'error' };
        }

        if (progress >= 7) {
            return this.progressSteps[6];
        }

        // case_progress = last completed step, so current active step = progress + 1
        // Array is 0-indexed, so active step index = (progress + 1) - 1 = progress
        const activeIndex = Math.max(0, Math.min(progress, 6));
        return this.progressSteps[activeIndex];
    }

    onSearchInput(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.applyFilter();
    }

    private applyFilter(): void {
        const term = this.searchTerm.toLowerCase().trim();
        if (!term) {
            this.filteredCases = [...this.cases];
            return;
        }
        this.filteredCases = this.cases.filter(c =>
            c.id.toString().toLowerCase().includes(term) ||
            (c.caseCode && c.caseCode.toLowerCase().includes(term)) ||
            c.clientName.toLowerCase().includes(term) ||
            c.attorney.toLowerCase().includes(term)
        );
    }

    viewCaseDetails(item: AssistantCase) {
        this.router.navigate(['/assistant-cases', item.id]);
    }
}
